import Loader from "components/DesignSystem/Loader/loader";
import { Button } from "components/DesignSystem/Button/Button";
import Modal from "components/DesignSystem/Modal/Modal";
import { Form, Formik, FormikHelpers, useFormikContext } from "formik";
import { AnimatePresence, motion } from "framer-motion";
import { useAppDispatch } from "hooks/useAppDispatch";
import { useAppSelector } from "hooks/useAppSelector";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useToast } from "hooks/useToast";
import { useEffect, useState } from "react";
import { RowSelectionState } from "react-table-8.10.7";
import {
  useBulkAssignMerchantMutation,
  useGetAllTransactionsDataQuery,
} from "store/apis/transactions";
import { useAddVendorsMutation } from "store/apis/vendors";
import { setTxnFilters } from "store/slices/transactionFilter";
import { BackendError } from "types/utils/error";
import { ModalProps } from "types/utils/modal";
import { object, string } from "yup";
import { TransactionsView } from "components/TransactionsView/TransactionsView";
import { MerchantSelector } from "./MerchantSelector";
import { useGetTransactionsFilterSet } from "hooks/useGetTransactionsFilterSet";

type BulkAssignVendorProps = ModalProps & {
  deselectRows: () => void;
  unSelectedRowsIds: string[];
  isAllTxnSelected: boolean;
};

export const MerchantComponent = () => {
  const { setValues, values } = useFormikContext<{
    vendorName: string;
    vendorId: string;
  }>();

  const { alertToast } = useToast();
  const entityId = useCurrentEntityId();
  const { uuid: groupId } = useCurrentGroupContext();
  const [addMerchant, { isLoading }] = useAddVendorsMutation();

  const onCreateMerchant = async (name: string) => {
    try {
      const { name: vendorName, uuid } = await addMerchant({
        groupId,
        entityId,
        payload: { name },
      }).unwrap();

      setValues({
        vendorName,
        vendorId: uuid,
      });
    } catch (error) {
      alertToast({ message: (error as BackendError)?.data?.error?.message });
    }
  };

  return (
    <MerchantSelector
      isLoading={isLoading}
      isDisabled={isLoading}
      merchantChange={async (values) => {
        setValues({
          vendorName: values?.data,
          vendorId: values?.merchantId || null,
        });
      }}
      value={
        values.vendorId
          ? {
              value: values.vendorId,
              label: values.vendorName,
            }
          : null
      }
      withForm
      name="vendorId"
      label="Vendor"
      placeholder="Select Vendor"
      menuPortalTarget={document.body}
      onCreateMerchant={onCreateMerchant}
      creatable
      styles={{
        menuPortal: (base) => ({
          ...base,
          width: 600,
        }),
      }}
    />
  );
};

export const DisclaimerUI = ({
  selectedVendor,
  txnCount,
}: {
  selectedVendor: string;
  txnCount: number;
}) => {
  return (
    <div className="t-text-body-sm">
      Are you sure you want to assign{" "}
      <AnimatePresence mode="wait">
        <motion.b
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ ease: "easeInOut", duration: 0.4 }}
          key={selectedVendor}
        >
          {" "}
          {selectedVendor}
        </motion.b>
      </AnimatePresence>{" "}
      as vendor for
      <motion.b
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ ease: "easeInOut", duration: 0.4 }}
        key={selectedVendor}
      >
        {" "}
        {txnCount}
      </motion.b>{" "}
      transactions?
    </div>
  );
};

export const BulkAssignVendor = ({
  close,
  isOpen,
  deselectRows,
  isAllTxnSelected,
  unSelectedRowsIds,
}: BulkAssignVendorProps) => {
  const entityId = useCurrentEntityId();
  const { uuid: groupId } = useCurrentGroupContext();
  const { alertToast, successToast } = useToast();
  const [bulkAssignVendor, { reset, data }] = useBulkAssignMerchantMutation();
  const dispatch = useAppDispatch();

  const { selectedTransactionIds } = useAppSelector(
    (store) => store.transactions
  );

  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

  useEffect(() => {
    if (selectedTransactionIds) {
      const selectedTxns = selectedTransactionIds.reduce(
        (accumulator, currentId, i) => {
          return { ...accumulator, [currentId]: true };
        },
        {}
      );
      setRowSelection(selectedTxns);
    }
  }, [selectedTransactionIds]);

  const selectedTxnIds = Object.keys(rowSelection);

  const { data: filterSet, isLoading: isFilterSetLoading } =
    useGetTransactionsFilterSet({
      filter_operation: "ASSIGN_VENDOR",
      unSelectedRowsIds,
      skip: !isAllTxnSelected || !isOpen,
    });

  const {
    txns_count = 0,
    txns_hash_key = "",
    vendor_assigned_txns_count = 0,
  } = filterSet || {};

  const { data: transactionData, isLoading } = useGetAllTransactionsDataQuery(
    {
      groupId,
      entityId,
      transactionIds: selectedTransactionIds.join(","),
    },
    {
      skip: !groupId || !entityId,
      refetchOnMountOrArgChange: true,
    }
  );

  const { transactions = [] } = transactionData || {};

  const onSubmit = async (
    {
      vendorName,
      vendorId,
    }: {
      vendorName: string;
      vendorId: string;
    },
    {
      resetForm,
    }: FormikHelpers<{
      vendorName: string;
      vendorId: string;
    }>
  ) => {
    try {
      if (isAllTxnSelected) {
        await bulkAssignVendor({
          groupId,
          entityId,
          payload: {
            entity_merchant_uuid: vendorId,
            transactions_hash_key: txns_hash_key,
          },
        }).unwrap();
      } else {
        await bulkAssignVendor({
          groupId,
          entityId,
          payload: {
            entity_merchant_uuid: vendorId,
            transaction_ids: selectedTxnIds.join(", "),
          },
        }).unwrap();
      }

      successToast({
        title: "Successful",
        message: `${vendorName} was assigned to
       ${selectedTxnIds.length} transactions`,
        action: {
          label: "View",
          onClick: () => {
            dispatch(
              setTxnFilters({
                vendors: [
                  data?.transactions_updated[0].merchant_data.name || "",
                ],
              })
            );
            deselectRows();
          },
        },
        onDismiss: () => {
          reset();
          deselectRows();
          setRowSelection({});
        },
        duration: 5000,
      });
      // successToast({
      //   title: "Vendor assigned successfully",
      //   message: `For ${selectedTxnIds.length} transactions`,
      // });
      close();
      deselectRows();
      setRowSelection({});
      resetForm();
    } catch (error) {
      alertToast({ message: (error as BackendError)?.data?.error?.message });
    }
  };

  return (
    <>
      <Modal.Root open={isOpen} onOpenChange={close}>
        <Formik
          initialValues={{ vendorName: "", vendorId: "" }}
          onSubmit={onSubmit}
          validateOnChange
          validateOnMount
          enableReinitialize
          validationSchema={object({
            vendorId: string().required("Please select a vendor"),
          })}
        >
          {({
            submitForm,
            isSubmitting,
            isValid,
            values: { vendorId, vendorName },
            resetForm,
          }) => {
            return (
              <Modal.Content
                useCustomOverlay
                size={Boolean(vendorId) ? "xl" : "large"}
              >
                <Modal.Header className="sm:t-pl-4">
                  <Modal.Title>Assign Vendor</Modal.Title>
                  <Modal.Close />
                </Modal.Header>
                <Modal.Body className="sm:t-px-4">
                  <Form className="t-m-0 t-flex t-flex-col t-gap-4">
                    {isFilterSetLoading ? (
                      <Loader size="small" />
                    ) : (
                      <>
                        <MerchantComponent />
                        {vendorId && (
                          <AnimatePresence>
                            <DisclaimerUI
                              txnCount={selectedTxnIds.length}
                              selectedVendor={vendorName || ""}
                            />

                            <motion.div
                              layout
                              initial={{ opacity: 0, y: 100 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{
                                ease: "easeInOut",
                                duration: 0.5,
                              }}
                            >
                              {isLoading ? (
                                <Loader />
                              ) : (
                                <TransactionsView
                                  transactions={transactions}
                                  rowSelection={rowSelection}
                                  setRowSelection={setRowSelection}
                                  showCheckbox
                                />
                              )}
                            </motion.div>
                          </AnimatePresence>
                        )}
                      </>
                    )}
                  </Form>
                </Modal.Body>
                <Modal.Footer className="sm:t-px-4">
                  <div className="t-flex t-justify-end t-gap-3">
                    <Button
                      type="reset"
                      onClick={() => {
                        resetForm();
                        close();
                      }}
                      disabled={isSubmitting}
                    >
                      Cancel
                    </Button>
                    <Button
                      customType="primary"
                      onClick={submitForm}
                      isLoading={isSubmitting}
                      disabled={
                        isSubmitting ||
                        !isValid ||
                        Boolean(selectedTxnIds.length === 0)
                      }
                    >
                      Assign
                    </Button>
                  </div>
                </Modal.Footer>
              </Modal.Content>
            );
          }}
        </Formik>
      </Modal.Root>
    </>
  );
};
